import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useBillingApi } from '../../api/billing/billing-context'
import { AiEulaAcceptanceRecord } from 'common-billing-server/types'
import { formatDateTime } from '../../format-date'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'

interface InstallationAiEulaProps {
    installationId: string
}

/** A user is blocked from the assistant until they accept the current version, whatever they accepted before. */
const acceptanceStatus = (acceptance: AiEulaAcceptanceRecord, acceptances: AiEulaAcceptanceRecord[]) => {
    if (acceptance.isCurrentVersion) return { label: 'Current', color: 'success' as const }
    const hasCurrent = acceptances.some((other) => other.edgeUserId === acceptance.edgeUserId && other.isCurrentVersion)
    return hasCurrent
        ? { label: 'Superseded', color: 'default' as const }
        : { label: 'Must re-accept', color: 'warning' as const }
}

export const InstallationAiEulaComponent = ({ installationId }: InstallationAiEulaProps) => {
    const billingApi = useBillingApi()
    const [acceptances, setAcceptances] = useState<AiEulaAcceptanceRecord[]>([])
    const [currentVersion, setCurrentVersion] = useState<string | undefined>()
    const [isLoading, setIsLoading] = useState(false)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        async function fetchAcceptances() {
            const operationId = 'fetch-ai-eula-acceptances'
            removeFailedOperation(operationId)
            setIsLoading(true)
            try {
                const response = await billingApi.getInstallationAiEulaAcceptances(installationId)
                setCurrentVersion(response.currentVersion)
                setAcceptances(response.acceptances)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed to fetch AI Assistant agreement acceptances',
                    error,
                    retryFn: () => void fetchAcceptances(),
                })
            }
            setIsLoading(false)
        }

        void fetchAcceptances()

        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [installationId])

    const userCount = new Set(acceptances.map((acceptance) => acceptance.edgeUserId)).size
    const currentCount = acceptances.filter((acceptance) => acceptance.isCurrentVersion).length

    return (
        <Paper style={{ padding: '20px' }}>
            <Typography variant="h6" gutterBottom>
                AI Assistant Agreement
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Current version: {currentVersion ?? '-'}. Acceptance is per user, installation and version; the
                assistant stays blocked for every user without an acceptance of the current version, including users who
                never opened it.
            </Typography>

            {errorBanner}

            {isLoading ? (
                <CircularProgress />
            ) : acceptances.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No user on this installation has accepted the AI Assistant agreement
                </Alert>
            ) : (
                <>
                    <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                        {currentCount} of {userCount} {userCount === 1 ? 'user has' : 'users have'} accepted the current
                        version
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Edge user ID</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Accepted</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acceptances.map((acceptance) => {
                                    const status = acceptanceStatus(acceptance, acceptances)
                                    return (
                                        <TableRow key={`${acceptance.edgeUserId}:${acceptance.version}`}>
                                            <TableCell>{acceptance.edgeUsername}</TableCell>
                                            <TableCell>{acceptance.edgeUserId}</TableCell>
                                            <TableCell>{acceptance.version}</TableCell>
                                            <TableCell>{formatDateTime(new Date(acceptance.acceptedAt))}</TableCell>
                                            <TableCell>
                                                <Chip label={status.label} color={status.color} size="small" />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Paper>
    )
}
